import React from "react";
import "98.css";
import styled from "styled-components";

const CONTROL_LABELS = {
  minimize: "Minimize",
  maximize: "Maximize",
  restore: "Restore",
  help: "Help",
  close: "Close",
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  /* Status-bar text ("Cards: 12 / 52") is chrome, not copy. */
  & > .status-bar {
    -webkit-user-select: none;
    user-select: none;
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
`;

// The grab cursor only where grabbing WORKS: draggable containers
// (PositionedWindow, TouchDragBox) stamp data-draggable, and only
// their title bars advertise dragging.
const TitleBar = styled.div`
  cursor: default;

  [data-draggable] & {
    cursor: grab;
  }
  [data-draggable] &:active {
    cursor: grabbing;
  }

  /* A programmatic press (the spectated close ritual) needs the same
     inset bevel as a real pointer-held 98.css button. */
  & .title-bar-controls button[data-pressed="true"]:not(:disabled) {
    box-shadow:
      inset -1px -1px #fff,
      inset 1px 1px #0a0a0a,
      inset -2px -2px #dfdfdf,
      inset 2px 2px grey;
  }
`;

const TitleText = styled.div`
  -webkit-user-select: none;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  white-space: nowrap;
`;

function Window({
  title,
  // Optional 16px icon node rendered at the left of the title bar,
  // like real Win95 windows. Pass a <PixelIcon size={16} />.
  icon,
  controls,
  disabledControls,
  pressedControls,
  onControlClick,
  inactive = false,
  statusFields,
  children,
  className,
  bodyStyle,
  ...delegated
}) {
  const hasControls = Array.isArray(controls) && controls.length > 0;
  const hasStatus = Array.isArray(statusFields) && statusFields.length > 0;
  const rootClassName = ["window", className].filter(Boolean).join(" ");

  return (
    <Wrapper className={rootClassName} {...delegated}>
      {title !== undefined && (
        <TitleBar
          className={inactive ? "title-bar inactive" : "title-bar"}
          data-drag-handle
        >
          <TitleText className="title-bar-text">
            {icon}
            {title}
          </TitleText>
          {hasControls && (
            <div className="title-bar-controls">
              {controls.map((control) => (
                <button
                  key={control}
                  aria-label={CONTROL_LABELS[control] ?? control}
                  disabled={disabledControls?.includes(control)}
                  data-pressed={
                    pressedControls?.includes(control) ? "true" : undefined
                  }
                  onClick={
                    onControlClick ? () => onControlClick(control) : undefined
                  }
                />
              ))}
            </div>
          )}
        </TitleBar>
      )}
      <Body className="window-body" style={bodyStyle}>
        {children}
      </Body>
      {hasStatus && (
        <div className="status-bar">
          {statusFields.map((field, i) => (
            <p key={i} className="status-bar-field">
              {field}
            </p>
          ))}
        </div>
      )}
    </Wrapper>
  );
}

export default Window;
